import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import mapboxgl from 'mapbox-gl';
import { Subject, Subscription, debounceTime } from 'rxjs';

import { EntityLabelsTranslationService } from '@gfe/core/services/entity-labels-translation/entity-labels-translation.service';
import { TooltipService } from '@gfe/shared/components/tooltip-singleton/tooltip.service';

import { MarkerDescription } from '@features/components/visualization/mapbox/models/marker-description';

/** Wide enough for a date line to sit on one row, narrow enough to leave the map readable. */
const TOOLTIP_MAX_WIDTH = 380;

@Injectable({
  providedIn: 'root'
})
export class MapboxTooltip {
  private closeTimeout: NodeJS.Timeout | null = null;
  private lastMouseEnterMarker = { id: 0, showDetailsClicked: false };
  private map!: mapboxgl.Map;
  private translate = inject(TranslateService);
  private entityLabelsTranslation = inject(EntityLabelsTranslationService);
  private tooltipService = inject(TooltipService);
  private closeEvent = new Subject();
  private subscription: Subscription | undefined;
  private currentPopupCleanup?: () => void;

  constructor() {
    this.subscription = this.closeEvent.pipe(debounceTime(200)).subscribe((value) => {
      if (value) this.tooltipService.hide();
    });
  }

  setMap(map: mapboxgl.Map) {
    this.map = map;
  }

  showTooltip(
    eventLng: number,
    coordinates: number[],
    description: MarkerDescription
  ): void {
    this.currentPopupCleanup?.();
    // A close still pending from the stop just left would fire against *this* popup: the timer
    // reads `currentPopupCleanup` when it runs, not when it was armed.
    this.clearTimeout();
    this.closeEvent.next(false);

    if (typeof description === 'string') {
      description = JSON.parse(description) as MarkerDescription;
    }

    while (Math.abs(eventLng - coordinates[0]) > 180) {
      coordinates[0] += eventLng > coordinates[0] ? 360 : -360;
    }

    const screenPoint = this.createMapVirtualElement(this.map, [
      coordinates[0],
      coordinates[1]
    ]);
    this.tooltipService.show(
      screenPoint as HTMLElement,
      this.popUpText(description, false),
      {
        position: 'top',
        title: description.name,
        titleSuffix: description.type,
        // Without a cap the box is `size-max` and grows to whatever the title needs, which is
        // what let a long uid run out past its own background. The title truncates against this.
        maxWidth: TOOLTIP_MAX_WIDTH,
        offset: 12,
        interactive: true,
        details: this.popUpText(description, true)
      }
    );
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: true,
      closeOnMove: false,
      offset: 12
    })
      .setLngLat([coordinates[0], coordinates[1]])
      .setHTML("<div class='hidden'></div>") // Set just to show the mouse pointer
      .addTo(this.map);

    //Use to add extra information when the user click in the popup button 'view more'
    //When the set HTML is called, the HTML inside the popup is reset, so the addEventListener is called only one time
    //The only option is to add another button that close the popup.
    document.getElementById('button-popup')?.addEventListener('click', () => {
      this.lastMouseEnterMarker.showDetailsClicked = true;
      popup.setHTML(this.popUpText(description, true));

      document.getElementById('close-button-popup')?.addEventListener('click', () => {
        this.lastMouseEnterMarker.showDetailsClicked = false;
        popup.remove(); //close the popup, so the HTML inside the popup is reset if the user click again on the popup
      });
      document.getElementById('copy-button-popup')?.addEventListener('click', (event) => {
        const text = (event.target as HTMLInputElement).dataset['text'] as string;
        navigator.clipboard.writeText(text);
      });
    });

    const popupElement = document.getElementsByClassName('mapboxgl-popup')[0];

    const onMouseOver = () => this.clearTimeout();
    const onMouseLeave = () => this.startCloseTimeout();
    const onMouseOut = () => this.startCloseTimeout();

    popupElement.addEventListener('mouseover', onMouseOver);
    popupElement.addEventListener('mouseleave', onMouseLeave);
    popupElement.addEventListener('mouseout', onMouseOut);

    let isCleaningUp = false;

    const handlePopupClose = () => {
      if (!isCleaningUp) {
        this.currentPopupCleanup?.();
      }
    };

    this.currentPopupCleanup = () => {
      if (isCleaningUp) return;
      isCleaningUp = true;

      popup.off('close', handlePopupClose);

      popupElement.removeEventListener('mouseover', onMouseOver);
      popupElement.removeEventListener('mouseleave', onMouseLeave);
      popupElement.removeEventListener('mouseout', onMouseOut);
      popup.remove();

      this.currentPopupCleanup = undefined;
    };

    popup.on('close', handlePopupClose);
  }

  // returns a virtual element that Floating UI can use
  private createMapVirtualElement(map: mapboxgl.Map, lngLat: [number, number]): unknown {
    const point = map.project(lngLat); // { x, y } relative to map container
    const containerRect = map.getContainer().getBoundingClientRect();

    // convert to viewport coordinates
    const clientX = containerRect.left + point.x;
    const clientY = containerRect.top + point.y;

    return {
      getBoundingClientRect: () =>
        ({
          width: 0,
          height: 0,
          top: clientY,
          bottom: clientY,
          left: clientX,
          right: clientX,
          x: clientX,
          y: clientY
        }) as DOMRect,
      contextElement: map.getContainer() // optional
    } as unknown;
  }

  cleanup(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.closeEvent.complete();
    this.clearTimeout();
    this.currentPopupCleanup?.();
  }

  startCloseTimeout() {
    if (this.lastMouseEnterMarker.showDetailsClicked) {
      this.closeEvent.next(false);
      this.clearTimeout();
      return;
    }
    this.closeEvent.next(true);
    this.closeTimeout = setTimeout(() => this.currentPopupCleanup?.(), 200);
  }

  private clearTimeout(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  private popUpText(descriptionMarker: MarkerDescription, showFull: boolean): string {
    if (descriptionMarker) {
      const textVersion = [];
      textVersion.push(descriptionMarker.name);

      let listElement = `<ul class="flex flex-col list-inside">`;

      // Translate the labels before rendering
      const translatedLabels = this.entityLabelsTranslation.translateMarkerLabels(
        descriptionMarker.content
      );

      translatedLabels.forEach((labelItem) => {
        if (labelItem.value !== undefined && labelItem.value !== '') {
          listElement += `<li class="pb-2 text-xs inline-flex"><span class="font-semibold">${labelItem.label}</span>:<span class="font-light ml-1">${labelItem.value}</span></li>`;
          textVersion.push(`${labelItem.label}: ${labelItem.value}`);
        }
      });
      listElement += '</ul>';
      let startDateHtml = '';
      let endDateHtml = '';
      if (descriptionMarker.startDate && descriptionMarker.endDate) {
        const startDate = new Date(descriptionMarker.startDate).toLocaleString(
          this.translate.getCurrentLang(),
          {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
          }
        );

        const endDate = new Date(descriptionMarker.endDate).toLocaleString(
          this.translate.getCurrentLang(),
          {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
          }
        );

        const translatedStartDateLabel = this.translate.instant(
          'visualization.labels.start_date'
        );
        const translatedEndDateLabel = this.translate.instant(
          'visualization.labels.end_date'
        );

        startDateHtml = `<h2 class="text-xs mb-1 font-light inline-flex"><span class="font-semibold">${translatedStartDateLabel}</span>: ${startDate}</h2>`;
        endDateHtml = `<h2 class="text-xs font-light inline-flex mb-2"><span class="font-semibold">${translatedEndDateLabel}</span>: ${endDate}</h2>`;

        textVersion.push(`${translatedStartDateLabel}: ${startDate}`);
        textVersion.push(`${translatedEndDateLabel}: ${endDate}`);
      }
      if (showFull) {
        const descriptionPopUp = `${startDateHtml} ${endDateHtml} ${listElement}`;
        return `<div class="flex flex-col items-start p-1">${descriptionPopUp}</div><div></div>`;
      } else {
        const descriptionPopUp = `${startDateHtml} ${endDateHtml}`;
        return `<div class="flex flex-col items-start p-1">${descriptionPopUp}</div>`;
      }
    }
    return `<p class="p-0 text-xs font-light">We cannot display accurate information for this item because it has been
    moved. Please recompute the solution.</p>`;
  }
}
